import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useBrand } from '../contexts/BrandContext';
import {
  createCtiDeployment,
  extractApiError,
  CTI_PROFILES,
  CTI_VM_TEMPLATES,
  CtiProfile,
} from '../services/api';

// VM size hints mirror terraform/cape-sandbox.tf cape_template_specs so the
// operator sees what each template costs before launching.
const VM_TEMPLATE_HINTS: Record<string, string> = {
  CAPE_S: '4 vCPU · 16 GiB · 200 GB — 1–2 parallele Guests',
  CAPE_M: '8 vCPU · 24 GiB · 400 GB — 2–3 parallele Guests',
  CAPE_L: '12 vCPU · 48 GiB · 800 GB — 5–6 parallele Guests',
  CAPE_XL: '16 vCPU · 64 GiB · 1600 GB — 8+ parallele Guests',
};

const PROFILE_HINTS: Record<CtiProfile, string> = {
  basic: 'YARA / Capa / FileInfo / ClamAV / Orchestrator',
  cape: 'CAPEv2 dynamische Sandbox (benötigt /dev/kvm)',
  sandbox: 'SecTepe Sandbox-Controller + KVM-Worker (benötigt /dev/kvm)',
  misp: 'MISP Threat-Intel-Plattform',
  opencti: 'OpenCTI Threat-Intel-Plattform',
};

const Schema = z.object({
  vmTemplate: z.enum(CTI_VM_TEMPLATES),
  profiles: z.array(z.enum(CTI_PROFILES)).min(1, 'Mindestens ein Profil wählen'),
  // Empty string → platform-global (stripped before submit).
  tenantSlug: z
    .string()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/, 'DNS-kompatibel, 3–32 Zeichen')
    .optional()
    .or(z.literal('')),
  region: z.string().max(120).optional().or(z.literal('')),
  domain: z.string().max(253).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

export function CreateCtiDeployment() {
  const navigate = useNavigate();
  const { domain } = useBrand();
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { vmTemplate: 'CAPE_S', profiles: ['basic', 'cape', 'sandbox'], domain: '' },
  });

  const selectedTemplate = watch('vmTemplate');

  const onSubmit = async (data: Form) => {
    setSubmitting(true); setErr(null);
    try {
      await createCtiDeployment({
        vmTemplate: data.vmTemplate,
        profiles: data.profiles,
        tenantSlug: data.tenantSlug || undefined,
        region: data.region || undefined,
        domain: data.domain || undefined,
      });
      navigate('/cti-deployments');
    } catch (e) {
      setErr(extractApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded shadow p-6 max-w-2xl space-y-4">
      <h2 className="text-xl font-semibold">CTI-Stack deployen</h2>
      <p className="text-sm text-slate-500">
        Provisioniert eine frische nested-KVM-VM (IONOS) und deployt den
        SecTepe.Core.CTI-Stack via Ansible/AWX.
      </p>

      <fieldset className="border rounded p-3">
        <legend className="text-sm font-medium px-1">VM-Template</legend>
        <select {...register('vmTemplate')} className="w-full px-2 py-1 border rounded font-mono">
          {CTI_VM_TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-1">{VM_TEMPLATE_HINTS[selectedTemplate]}</p>
      </fieldset>

      <fieldset className="border rounded p-3">
        <legend className="text-sm font-medium px-1">Komponenten-Profile</legend>
        {CTI_PROFILES.map((p) => (
          <label key={p} className="flex items-start gap-2 text-sm py-0.5">
            <input type="checkbox" value={p} {...register('profiles')} className="mt-1" />
            <span>
              <span className="font-medium font-mono">{p}</span>
              <span className="text-slate-400"> · {PROFILE_HINTS[p]}</span>
            </span>
          </label>
        ))}
        {errors.profiles && <p className="text-red-600 text-sm mt-1">{errors.profiles.message}</p>}
      </fieldset>

      <div>
        <label className="block text-sm font-medium mb-1">
          Tenant-Slug <span className="text-slate-400">(optional — leer = plattformweit)</span>
        </label>
        <input {...register('tenantSlug')} className="w-full px-2 py-1 border rounded font-mono" placeholder="acme-corp" />
        {errors.tenantSlug && <p className="text-red-600 text-sm">{errors.tenantSlug.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">IONOS-Region <span className="text-slate-400">(optional)</span></label>
        <input {...register('region')} className="w-full px-2 py-1 border rounded font-mono" placeholder="de/txl" />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Domain <span className="text-slate-400">(optional, Traefik-Routing)</span></label>
        <input {...register('domain')} className="w-full px-2 py-1 border rounded font-mono" placeholder={`cti.${domain}`} />
      </div>

      {err && <div className="bg-red-100 text-red-700 p-2 rounded">{err}</div>}

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={submitting} className="btn-primary">
          {submitting ? 'AWX-Job startet …' : 'Deployen'}
        </button>
        <button type="button" onClick={() => navigate('/cti-deployments')} className="btn-secondary">Abbrechen</button>
      </div>
    </form>
  );
}
